'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import {
  CSSProperties,
  MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ITEM_HEIGHT, MID_HEIGHT } from '@/lib/components/OrderBook/constants';
import { EmptyBids } from '@/lib/components/OrderBook/EmpteBids';
import { EmptyAsks } from '@/lib/components/OrderBook/EmptyAsks';
import { EmptyOrderBook } from '@/lib/components/OrderBook/EmptyOrderBook';
import { HoverSummaryOverlay } from '@/lib/components/OrderBook/HoverSummaryOverlay';
import { OrderBookHeader } from '@/lib/components/OrderBook/OrderBookHeader';
import { OrderBookListItem } from '@/lib/components/OrderBook/OrderBookListItem';
import {
  OrderBookMobileActions,
  OrderBookMobileTab,
} from '@/lib/components/OrderBook/OrderBookMobileTabs';
import { OrderBookSpread } from '@/lib/components/OrderBook/OrderBookSpread';
import { useIsMobile } from '@/lib/hooks/useIsMobile';
import { useOrderBookDisplayData } from '@/lib/hooks/useOrderBookDisplayData';
import { useOrderBooksServices } from '@/lib/providers/OrderBookStoreProvider';
import { useOrderFormStore } from '@/lib/store/order-form-store';
import { useOutcomePrices } from '@/lib/store/prices-store';
import { EMarketOrderType } from '@/lib/types/market-order-type';
import { Skeleton } from '@/lib/ui-kit/skeleton';
import { cn } from '@/lib/ui-kit/utils';
import { Tabs } from '@/lib/ui-kit/vercel-tabs';
import {
  getWithCumulativeTotals,
  PriceVolumeCumulative,
} from '@/lib/utils/getWithCumulativeTotals';

type SpecialRowType = 'spread' | 'empty-asks' | 'empty-bids';
type OrderBookRow = PriceVolumeCumulative | SpecialRowType;

// Stable per-row key so the virtualizer can reconcile items across updates.
// Prices are unique within a side, so `side:price` is a safe identity.
const getRowKey = (item: OrderBookRow): string => {
  if (typeof item === 'string') return item;
  return `${item.side}:${item.price.toString()}`;
};

export type OrderBookProps = {
  outcomeId: string;
  className?: string;
  containerClassName?: string;
  scrollClassName?: string;
};

export const OrderBook = ({
  outcomeId,
  className,
  containerClassName,
  scrollClassName,
}: OrderBookProps) => {
  const { intent, action, setAll } = useOrderFormStore();
  const { asks, bids, initialised } = useOrderBookDisplayData(outcomeId, intent);
  const livePrices = useOutcomePrices(outcomeId);
  const [tooltipY, setTooltipY] = useState(0);
  const [tooltipX, setTooltipX] = useState(0);
  const isMobile = useIsMobile();
  const [hoveredItem, setHoveredItem] = useState<PriceVolumeCumulative | null>(null);
  const [hoverStart, setHoverStart] = useState(0);
  const [hoverEnd, setHoverEnd] = useState(0);
  const [mobileActiveTab, setMobileActiveTab] = useState<OrderBookMobileTab>('yes');
  const [roundToCents, setRoundToCents] = useState(false);
  const t = useTranslations('orderBook');

  const handleMobileTabChange = useCallback(
    (tab: OrderBookMobileTab) => {
      setMobileActiveTab(tab);
      setAll({ intent: tab });
    },
    [setAll],
  );

  // Sync mobile tab with intent from store
  useEffect(() => {
    if (intent !== mobileActiveTab) {
      setMobileActiveTab(intent as OrderBookMobileTab);
    }
  }, [intent, mobileActiveTab]);

  // Keep services available for data derivation hooks; socket updates happen in provider.
  useOrderBooksServices();

  const midRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoveredItemElemRef = useRef<HTMLDivElement | null>(null);

  const asksCount = asks.length;
  const bidsCount = bids.length;
  const isEmpty = asksCount === 0 && bidsCount === 0;
  const spreadItemIndex = asks.length || 1;
  const hasScrolledRef = useRef<boolean>(false);
  const prevSpreadIndexRef = useRef<number>(spreadItemIndex);
  const prevIntentRef = useRef(intent);

  // Reset scroll tracking SYNCHRONOUSLY during render when intent flips. A
  // post-paint useEffect would be too late: the scroll-anchoring useLayoutEffect
  // below fires first and shifts scrollTop by the huge stale-vs-new delta
  // (visible flick) before any post-render effect runs. Doing it here ensures
  // anchoring early-returns and only the centering effect re-scrolls.
  if (prevIntentRef.current !== intent) {
    prevIntentRef.current = intent;
    hasScrolledRef.current = false;
    // Seed prev to the new spread so anchoring computes delta=0 on first
    // fire after the flip — no spurious shifts even if hasScrolledRef
    // re-flips to true before the next intent change.
    prevSpreadIndexRef.current = spreadItemIndex;
  }

  // An empty book unmounts the scroll container; re-center on the one mounted next.
  if (isEmpty) hasScrolledRef.current = false;

  // Center the spread row (best bid / best ask) in the scroll container
  const centerSpread = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (!scrollContainerRef.current) return;

      const containerHeight = scrollContainerRef.current.clientHeight;
      const spreadTop = asksCount * ITEM_HEIGHT;
      const targetScrollTop = spreadTop - (containerHeight - MID_HEIGHT) / 2;

      scrollContainerRef.current.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior,
      });
    },
    [asksCount],
  );

  // Scroll to center the spread row on initial load
  useEffect(() => {
    if (!scrollContainerRef.current || !initialised || hasScrolledRef.current) return;
    if (asksCount === 0 && bidsCount === 0) return;

    requestAnimationFrame(() => {
      if (!scrollContainerRef.current) return;

      centerSpread();
      hasScrolledRef.current = true;
      prevSpreadIndexRef.current = spreadItemIndex;
    });
  }, [asksCount, bidsCount, initialised, spreadItemIndex, centerSpread]);

  // Scroll anchoring: keep the spread row visually fixed when asks above it
  // change (e.g. best ask is matched and removed). Without this the list would
  // "jump" because the spread's offset from the top of the scroll content
  // changes while scrollTop stays the same.
  useLayoutEffect(() => {
    const prev = prevSpreadIndexRef.current;
    prevSpreadIndexRef.current = spreadItemIndex;

    // Skip until the initial centering scroll has run — that effect seeds the
    // baseline scrollTop and prevSpreadIndexRef itself.
    if (!hasScrolledRef.current) return;
    if (!scrollContainerRef.current) return;

    const delta = spreadItemIndex - prev;
    if (delta === 0) return;

    scrollContainerRef.current.scrollTop += delta * ITEM_HEIGHT;
  }, [spreadItemIndex]);

  const handleItemHover = useCallback(
    (event: MouseEvent, item: PriceVolumeCumulative, rowIndex: number) => {
      if (isMobile) {
        return;
      }
      const triggerElem = event.currentTarget as HTMLDivElement;
      hoveredItemElemRef.current = triggerElem;
      const rect = triggerElem.getBoundingClientRect();
      setTooltipY(rect.y + rect.height * 0.5);
      setTooltipX(rect.x + 72);

      const start =
        item.side === 'asks' ? rowIndex * ITEM_HEIGHT : (spreadItemIndex + 1) * ITEM_HEIGHT;
      const end =
        item.side === 'bids' ? (rowIndex + 1) * ITEM_HEIGHT : spreadItemIndex * ITEM_HEIGHT;

      setHoverStart(start);
      setHoverEnd(end);
      setHoveredItem(item);
    },
    [spreadItemIndex, isMobile],
  );

  const cumulativeAsks = useMemo(() => {
    return getWithCumulativeTotals(asks, 'asks');
  }, [asks]);

  const cumulativeBids = useMemo(() => {
    return getWithCumulativeTotals(bids, 'bids');
  }, [bids]);

  const asksMaxVolume = useMemo(() => {
    if (cumulativeAsks.length === 0) {
      return Decimal(0);
    }
    return cumulativeAsks.at(0)!.cumulativeVolume;
  }, [cumulativeAsks]);

  const bidsMaxVolume = useMemo(() => {
    if (cumulativeBids.length === 0) {
      return Decimal(0);
    }
    return cumulativeBids.at(-1)!.cumulativeVolume;
  }, [cumulativeBids]);

  // Use PRICES_DATA for best ask/bid with fallback to order book data
  const minAsk = useMemo(() => {
    // Prefer PRICES_DATA bestAsk
    if (typeof livePrices?.bestAsk === 'number') {
      return Decimal(livePrices.bestAsk);
    }
    // Fallback to order book data
    return asks.length ? Decimal.min(...asks.map((a) => a.price)) : null;
  }, [asks, livePrices?.bestAsk]);

  const maxBid = useMemo(() => {
    // Prefer PRICES_DATA bestBid
    if (typeof livePrices?.bestBid === 'number') {
      return Decimal(livePrices.bestBid);
    }
    // Fallback to order book data
    return bids.length ? Decimal.max(...bids.map((a) => a.price)) : null;
  }, [bids, livePrices?.bestBid]);

  // Last price from PRICES_DATA (chance field represents current/last price)
  const lastPrice = useMemo(() => {
    if (typeof livePrices?.chance === 'number') {
      return Decimal(livePrices.chance);
    }
    return null;
  }, [livePrices?.chance]);

  const handleRowClick = useCallback(
    (item: PriceVolumeCumulative) => {
      let matchingCount = Decimal(0);
      if (action === 'buy' && item.side === 'asks') {
        matchingCount = item.cumulativeShares;
      } else if (action === 'sell' && item.side === 'bids') {
        matchingCount = item.cumulativeShares;
      }
      setAll({
        orderType: EMarketOrderType.LIMIT,
        sharesCount: matchingCount,
        limitPrice: item.price.mul(100),
        isMobileFormOpened: true,
      });
    },
    [action, setAll],
  );

  const allItems: OrderBookRow[] = useMemo(() => {
    // Always show all (both asks and bids)
    return [
      ...(cumulativeAsks.length === 0 ? ['empty-asks'] : cumulativeAsks),
      'spread',
      ...(cumulativeBids.length === 0 ? ['empty-bids'] : cumulativeBids),
    ] as OrderBookRow[];
  }, [cumulativeAsks, cumulativeBids]);

  const bestAskItem = cumulativeAsks[cumulativeAsks.length - 1] ?? null;
  const bestBidItem = cumulativeBids[0] ?? null;

  // Keep a ref to the latest allItems so getItemKey (captured by the virtualizer)
  // can read the current row without a new closure on every render.
  const allItemsRef = useRef(allItems);
  allItemsRef.current = allItems;

  // CRITICAL: getItemKey must be referentially stable. @tanstack/virtual-core's
  // `getMeasurementOptions` memo includes getItemKey in its deps and calls
  // `notify` → `flushSync(rerender)` whenever the reference changes. Passing a
  // new closure every render causes an infinite re-render loop. Using
  // useCallback with an empty deps list + a ref for the data keeps it stable.
  const getItemKey = useCallback((index: number) => {
    const row = allItemsRef.current[index];
    return row !== undefined ? getRowKey(row) : index;
  }, []);

  const estimateSize = useCallback(() => ITEM_HEIGHT, []);
  const getScrollElement = useCallback(() => scrollContainerRef.current, []);

  // All rows share a fixed height (ITEM_HEIGHT = 32px for asks, bids, spread,
  // and empty placeholders), so the virtualizer doesn't need DOM measurement.
  const rowVirtualizer = useVirtualizer({
    count: allItems.length,
    getScrollElement,
    estimateSize,
    overscan: 6,
    getItemKey,
    // Disabling drops the stored scroll offset: otherwise the remounted container (scrollTop 0)
    // inherits the old one and rows render outside the viewport, leaving a blank book.
    enabled: !isEmpty,
    // React 19 rejects flushSync during render (row count changes mid-render); fixed rows don't need it.
    useFlushSync: false,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  return (
    <>
      <HoverSummaryOverlay
        cumulativeAsks={cumulativeAsks}
        cumulativeBids={cumulativeBids}
        hoveredItem={hoveredItem}
        tooltipX={tooltipX}
        tooltipY={tooltipY}
      />
      <div
        className={cn('bg-background relative flex flex-col', containerClassName ?? 'h-[320px]')}
        ref={containerRef}
      >
        {/* Mobile tabs with action buttons */}
        <div className="mb-4 flex items-center justify-between gap-2 px-3 min-[361px]:hidden">
          <div className="min-w-0 flex-1">
            <Tabs
              tabClassName="!body-small"
              tabs={[
                { id: 'yes', label: t('tradeYes') },
                { id: 'no', label: t('tradeNo') },
              ]}
              activeTab={mobileActiveTab}
              onTabChange={(tabId) => handleMobileTabChange(tabId as OrderBookMobileTab)}
              hideBottomBorder
              activeIndicatorClassName="bg-white"
            />
          </div>
          <OrderBookMobileActions
            roundToCents={roundToCents}
            onRoundToCentsChange={setRoundToCents}
            className="shrink-0"
          />
        </div>

        <OrderBookHeader
          label={t(intent === 'no' ? 'tradeNo' : 'tradeYes')}
          onCenter={() => centerSpread('smooth')}
        />
        {isEmpty ? (
          <EmptyOrderBook
            className={cn('flex flex-col', containerClassName ? 'flex-1' : 'h-[288px]')}
          />
        ) : (
          <div
            className={cn(
              scrollClassName ?? 'h-[288px]',
              'scrollbar-hide overflow-auto',
              className,
            )}
            ref={scrollContainerRef}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {initialised ? (
              <div
                // Re-mount the rows container on intent flip so the new YES/NO
                // book softly fades in instead of snapping instantly. Live
                // WebSocket updates within one intent don't trigger this.
                // `backwards` fill-mode keeps the element at the animation's
                // first keyframe (opacity 0) from mount, avoiding a 1-frame
                // flash at full opacity before the animation kicks in.
                key={intent}
                className="relative w-full fade-in animate-in duration-200 ease-out"
                style={{ height: `${totalSize}px`, animationFillMode: 'backwards' }}
              >
                {hoveredItem && (
                  <div
                    className={cn('pointer-events-none absolute inset-x-0 z-10 border-[#043673]', {
                      'border-t': hoveredItem.side === 'asks',
                      'border-b': hoveredItem.side === 'bids',
                    })}
                    style={{
                      height: `${hoverEnd - hoverStart}px`,
                      top: `${hoverStart}px`,
                      backgroundColor: 'rgba(90, 173, 245, 0.05)',
                      border: '1px solid rgba(0, 123, 255, 0.30)',
                    }}
                  ></div>
                )}
                {virtualRows.map((virtualRow) => {
                  const item = allItems[virtualRow.index];
                  if (item === undefined) return null;

                  const rowStyle: CSSProperties = {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  };

                  if (typeof item === 'object') {
                    const isBestAsk = item === bestAskItem;
                    const isBestBid = item === bestBidItem;
                    return (
                      <OrderBookListItem
                        key={virtualRow.key}
                        onClick={() => handleRowClick(item)}
                        item={item}
                        best={item.side === 'asks' ? isBestAsk : isBestBid}
                        side={item.side}
                        max={item.side === 'asks' ? Number(asksMaxVolume) : Number(bidsMaxVolume)}
                        onMouseEnter={(e) => handleItemHover(e, item, virtualRow.index)}
                        roundToCents={roundToCents}
                        style={rowStyle}
                      />
                    );
                  }
                  if (item === 'spread') {
                    return (
                      <OrderBookSpread
                        key={virtualRow.key}
                        ref={midRef}
                        minAsk={minAsk}
                        maxBid={maxBid}
                        lastPrice={lastPrice}
                        onMouseEnter={() => setHoveredItem(null)}
                        style={rowStyle}
                      />
                    );
                  }
                  if (item === 'empty-asks') {
                    return <EmptyAsks key={virtualRow.key} style={rowStyle} />;
                  }
                  if (item === 'empty-bids') {
                    return <EmptyBids key={virtualRow.key} style={rowStyle} />;
                  }
                  return null;
                })}
              </div>
            ) : (
              <Skeleton className="h-full w-full" />
            )}
          </div>
        )}
      </div>
    </>
  );
};
